import React from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { canAccessPath, ROLE_CONFIG } from "../../config/adminRoles";
import {
  HomeIcon,
  ArrowRightOnRectangleIcon,
  UserGroupIcon,
  BuildingOffice2Icon,
  BuildingStorefrontIcon,
  DocumentTextIcon,
  TagIcon,
  NewspaperIcon,
  BookmarkSquareIcon,
  ShieldCheckIcon,
  ChatBubbleLeftRightIcon,
  ReceiptPercentIcon,
  CommandLineIcon,
} from "@heroicons/react/24/outline";

/** Order matters: dividers appear between consecutive sections when both have at least one visible link. */
const NAV_SECTIONS = [
  [
    {
      path: "/dashboard",
      activePaths: ["/dashboard"],
      label: "Dashboard",
      icon: HomeIcon,
    },
  ],
  [
    {
      path: "/dashboard/users",
      activePaths: ["/dashboard/users"],
      matchPrefix: true,
      label: "Users",
      icon: UserGroupIcon,
    },
    {
      path: "/dashboard/organizations",
      activePaths: ["/dashboard/organizations"],
      matchPrefix: true,
      label: "Organizations",
      icon: BuildingOffice2Icon,
    },
    {
      path: "/dashboard/companies",
      activePaths: ["/dashboard/companies"],
      matchPrefix: true,
      label: "Companies",
      icon: BuildingStorefrontIcon,
    },
  ],
  [
    {
      path: "/dashboard/blogs",
      activePaths: ["/dashboard/blogs"],
      matchPrefix: true,
      label: "Blogs",
      icon: DocumentTextIcon,
    },
    {
      path: "/dashboard/blog-categories",
      activePaths: ["/dashboard/blog-categories"],
      matchPrefix: true,
      label: "Blog Categories",
      icon: TagIcon,
    },
    {
      path: "/dashboard/haystackers-blogs",
      activePaths: [
        "/dashboard/haystackers-blogs",
        "/dashboard/haystackers-blogs-details",
      ],
      matchPrefix: true,
      label: "Haystackers Blogs",
      icon: NewspaperIcon,
    },
    {
      path: "/dashboard/haystackers-blog-categories",
      activePaths: ["/dashboard/haystackers-blog-categories"],
      matchPrefix: true,
      label: "Haystackers Blog Categories",
      icon: BookmarkSquareIcon,
    },
  ],
  [
    {
      path: "/dashboard/support-tickets",
      activePaths: ["/dashboard/support-tickets"],
      label: "Support Tickets",
      icon: ChatBubbleLeftRightIcon,
    },
    {
      path: "/dashboard/coupons",
      activePaths: ["/dashboard/coupons"],
      label: "Coupons",
      icon: ReceiptPercentIcon,
    },
  ],
  [
    {
      path: "/dashboard/migrations",
      activePaths: ["/dashboard/migrations"],
      label: "Migrations",
      icon: CommandLineIcon,
    },
  ],
];

/** Pinned above the profile footer — not in the scrollable nav list. */
const SYSTEM_USERS_LINK = {
  path: "/dashboard/system-users",
  activePaths: ["/dashboard/system-users"],
  label: "System Users",
  icon: ShieldCheckIcon,
};

const Sidebar = () => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const visibleSections = NAV_SECTIONS.map((section) =>
    section.filter((item) => canAccessPath(user?.role, item.path)),
  ).filter((section) => section.length > 0);

  const showSystemUsers = canAccessPath(user?.role, SYSTEM_USERS_LINK.path);

  const handleLogout = async () => {
    try {
      await logout();
      navigate("/login");
    } catch (error) {
      console.error("Logout failed:", error);
    }
  };

  const isItemActive = (activePaths, matchPrefix) => {
    if (matchPrefix && activePaths?.length) {
      return activePaths.some(
        (path) =>
          location.pathname === path ||
          location.pathname.startsWith(path + "/"),
      );
    }
    return activePaths.some((path) => location.pathname === path);
  };

  return (
    <div className="h-full min-h-0 bg-white border-r border-gray-200 flex flex-col shadow-sm">
      <div className="flex-shrink-0 px-3 pt-3 pb-2">
        <p className="font-semibold text-gray-900 text-xl italic px-1 truncate">
          EzSignly
        </p>
      </div>

      <nav
        className="flex flex-1 flex-col min-h-0"
        aria-label="Main navigation"
      >
        <div className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden px-2 pb-2">
          <div className="space-y-0.5">
            {visibleSections.map((section, sectionIdx) => (
              <div key={sectionIdx}>
                {sectionIdx > 0 && (
                  <div
                    className="my-2 border-t border-gray-200"
                    role="separator"
                    aria-hidden="true"
                  />
                )}
                {section.map((item) => (
                  <NavLink
                    key={item.path}
                    to={item.path}
                    end={item.path === "/dashboard"}
                    className={`flex items-center gap-2 px-2 py-1.5 rounded-md text-sm transition-colors ${
                      isItemActive(item.activePaths, item.matchPrefix)
                        ? "bg-primary text-white shadow-sm"
                        : "text-gray-700 hover:bg-gray-100 hover:text-gray-900"
                    }`}
                  >
                    <item.icon className="h-4 w-4 flex-shrink-0" />
                    <span className="truncate">{item.label}</span>
                  </NavLink>
                ))}
              </div>
            ))}
          </div>
        </div>

        {showSystemUsers && (
          <div className="flex-shrink-0 border-t border-gray-200 px-2 pt-2 pb-2">
            <NavLink
              to={SYSTEM_USERS_LINK.path}
              className={`flex items-center gap-2 px-2 py-1.5 rounded-md text-sm transition-colors ${
                isItemActive(SYSTEM_USERS_LINK.activePaths, undefined)
                  ? "bg-primary text-white shadow-sm"
                  : "text-gray-700 hover:bg-gray-100 hover:text-gray-900"
              }`}
            >
              <SYSTEM_USERS_LINK.icon className="h-4 w-4 flex-shrink-0" />
              <span className="truncate">{SYSTEM_USERS_LINK.label}</span>
            </NavLink>
          </div>
        )}
      </nav>

      <div className="flex-shrink-0 border-t border-gray-200 bg-gray-50/80 p-2 space-y-1.5">
        <div className="bg-gray-100/80 rounded-lg border border-gray-200 px-2 py-2 min-w-0">
          <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-500">
            Name
          </p>
          <p className="text-sm font-medium text-gray-900 truncate mt-0.5">
            {user?.name || "—"}
          </p>
          <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-500 mt-2">
            Role
          </p>
          <p className="text-xs font-medium text-gray-700 truncate mt-0.5">
            {user?.role
              ? ROLE_CONFIG[user.role]?.label || user.role
              : "—"}
          </p>
        </div>
        <button
          type="button"
          onClick={handleLogout}
          className="w-full flex items-center gap-2 px-2 py-1.5 rounded-md text-sm text-gray-700 hover:bg-gray-200/80 hover:text-gray-900 transition-colors"
        >
          <ArrowRightOnRectangleIcon className="h-4 w-4 flex-shrink-0" />
          <span>Logout</span>
        </button>
      </div>
    </div>
  );
};

export default Sidebar;
